import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Tone = 'neutral' | 'accent' | 'success' | 'warn' | 'danger';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  icon?: ReactNode;
}

const tones: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-fg-muted border-border',
  accent: 'bg-[color:var(--accent-soft)] text-accent border-transparent',
  success: 'bg-[color:var(--success-soft)] text-[color:var(--success)] border-transparent',
  warn: 'bg-[color:var(--warn-soft)] text-[color:var(--warn)] border-transparent',
  danger: 'bg-[color:var(--danger-soft)] text-[color:var(--danger)] border-transparent',
};

// forwardRef so ref-cloning wrappers (Tooltip) can target the badge (FH-008).
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', icon, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 h-5 px-1.5 text-2xs font-semibold uppercase tracking-wide rounded border',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {icon && <span className="inline-flex shrink-0">{icon}</span>}
      {children}
    </span>
  );
});
